import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Search, Bell, Heart, MessageCircle, Share2, Play, UserPlus, UserCheck, User, Loader2, Pencil, Plus } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useFeedInfinite } from '@/hooks/useFeed';
import { useVirtualFeed } from '@/hooks/useVirtualFeed';
import { useLikePost, useBookmarkPost } from '@/hooks/usePost';
import { useUserSuggestions, useFollowUser } from '@/hooks/useUser';
import { formatCount, timeAgo } from '@/lib/formatters';
import { getVideoSource } from '@/lib/video';
import { getPlainTextFromBody, getPostContent } from '@/lib/postUtils';
import { processAvatarUrl } from '@/lib/mediaUtils';
import VideoPlayer from '@/components/VideoPlayer';
import PostComposer from '@/components/PostComposer';
import CommentModal from '@/components/CommentModal';
import PostCard, { getPostUser, getPostImage, getLikesCount } from '@/components/PostCard';
import NotificationPanel from '@/components/NotificationPanel';
import type { Post, FeedFilter } from '@/types';

gsap.registerPlugin(ScrollTrigger);

const filterChips: { label: string; feedType: FeedFilter; path: string }[] = [
  { label: 'For You', feedType: 'forYou', path: '/feed/forYou' },
  { label: 'Following', feedType: 'following', path: '/feed/following' },
  { label: 'Trending', feedType: 'trending', path: '/feed/trending' },
  { label: 'Latest', feedType: 'latest', path: '/feed/latest' },
  { label: 'Discover', feedType: 'discover', path: '/feed/discover' },
];

/** Derive feed type from current URL path. */
function useFeedTypeFromPath(): FeedFilter {
  const { pathname } = useLocation();
  const segment = pathname.split('/').pop();
  if (segment === 'following') return 'following';
  if (segment === 'trending') return 'trending';
  if (segment === 'latest') return 'latest';
  if (segment === 'discover') return 'discover';
  return 'forYou';
}

/* ─── Feed item grouping ─────────────────────────────────────────────── */

/** A portrait video post or a shelf of them. */
type FeedItem =
  | { kind: 'post'; post: Post }
  | { kind: 'shelf'; posts: Post[]; id: string };

/** Returns true for post types that are always 9:16 portrait video. */
function isPortraitVideoPost(post: Post): boolean {
  const type = (post.type as string | undefined)?.toUpperCase() ?? '';
  if (['BURST', 'SHORT'].includes(type)) return true;
  if (type === 'VIDEO') {
    // Check raw video dimensions if available
    const videos = (post as Record<string, unknown>).videos as Array<Record<string, unknown>> | undefined;
    const w = (videos?.[0]?.widthPx as number) ?? (videos?.[0]?.width as number) ?? 0;
    const h = (videos?.[0]?.heightPx as number) ?? (videos?.[0]?.height as number) ?? 0;
    // If no dimensions, assume portrait (most short-form content is 9:16)
    if (w === 0 || h === 0) return true;
    return h > w;
  }
  return false;
}

/**
 * Groups consecutive portrait video posts into shelves (min 1 post).
 * Non-portrait posts are kept as individual items.
 */
function groupFeedItems(posts: Post[]): FeedItem[] {
  const items: FeedItem[] = [];
  let i = 0;
  while (i < posts.length) {
    const post = posts[i];
    if (!post) { i++; continue; }
    if (isPortraitVideoPost(post)) {
      // Collect run of portrait videos
      const run: Post[] = [];
      while (i < posts.length && isPortraitVideoPost(posts[i])) {
        run.push(posts[i]);
        i++;
      }
      items.push({ kind: 'shelf', posts: run, id: `shelf-${run[0].id}` });
    } else {
      items.push({ kind: 'post', post });
      i++;
    }
  }
  return items;
}

/* ─── Portrait video shelf component ────────────────────────────────── */

function PortraitVideoShelf({ posts, onNavigate, onComment, onShare }: {
  posts: Post[];
  onNavigate: (id: string) => void;
  onComment: (id: string) => void;
  onShare: (id: string) => void;
}) {
  return (
    <div className="mb-3.5">
      <div className="flex items-center gap-2 px-0 mb-2">
        <Play size={12} className="text-[#FF4D4D]" />
        <span className="text-xs font-semibold text-[#A7B0C8] uppercase tracking-wider">
          {posts.length === 1 ? 'Video' : `${posts.length} Videos`}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {posts.map((post) => {
          const author = getPostUser(post);
          const videoSource = getVideoSource(post);
          const thumbnail = videoSource?.poster || getVideoThumbnail(post);
          const content = getPostContent(post);
          const label = post.title || content?.slice(0, 40) || '';

          return (
            <div
              key={post.id}
              className="bg-[#141821] rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => onNavigate(post.id)}
            >
              {/* Thumbnail */}
              <div className="relative w-full aspect-[9/16] bg-[#0B0D10]">
                {thumbnail ? (
                  <img src={thumbnail} alt={label} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play size={24} className="text-[#A7B0C8]" />
                  </div>
                )}
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
                    <Play size={18} className="text-white ml-0.5" fill="white" />
                  </div>
                </div>
                {/* Type badge */}
                <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-black/60 text-[#FF4D4D]">
                  {(post.type as string ?? 'Video').toLowerCase()}
                </span>
              </div>

              {/* Info */}
              <div className="p-2">
                <div className="flex items-center gap-1.5 mb-1">
                  {author.avatar ? (
                    <img src={author.avatar} alt={author.name} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#A38BFF]/20 flex items-center justify-center flex-shrink-0">
                      <User size={10} className="text-[#A38BFF]" />
                    </div>
                  )}
                  <span className="text-[10px] text-[#A7B0C8] truncate">{author.name}</span>
                </div>
                {label && (
                  <p className="text-[10px] text-[#F5F7FF] line-clamp-2 leading-tight mb-1.5">{label}</p>
                )}
                <div className="flex items-center gap-2 text-[#A7B0C8]">
                  <button
                    aria-label="Comment"
                    onClick={(e) => { e.stopPropagation(); onComment(post.id); }}
                    className="flex items-center gap-0.5"
                  >
                    <MessageCircle size={12} />
                    <span className="text-[10px]">{formatCount(post.commentCount ?? 0)}</span>
                  </button>
                  <button
                    aria-label="Share"
                    onClick={(e) => { e.stopPropagation(); onShare(post.id); }}
                    className="flex items-center gap-0.5"
                  >
                    <Share2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Get video thumbnail URL from a post's videos array or videoUrls. */
function getVideoThumbnail(post: Post): string | undefined {
  // SDK videoUrls.thumbnail (if populated)
  if (post.videoUrls?.thumbnail) return post.videoUrls.thumbnail;
  // Raw API: videos[].thumbnail.url
  const videos = (post as Record<string, unknown>).videos as Array<Record<string, unknown>> | undefined;
  if (videos && videos.length > 0) {
    const thumb = videos[0]?.thumbnail as Record<string, unknown> | undefined;
    if (thumb?.url) return thumb.url as string;
  }
  // Fallback to first image
  return getPostImage(post);
}

/** Expandable text — shows 2 lines with inline "more..." like Instagram. */
function ExpandableText({ text, className }: { text: string; className?: string }) {
  const [expanded, setExpanded] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const [clamped, setClamped] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  if (expanded) {
    return <p className={`${className ?? ''} whitespace-pre-line`}>{text}</p>;
  }

  return (
    <p className={`${className ?? ''} relative`}>
      <span ref={textRef} className="whitespace-pre-line line-clamp-2">
        {text}
      </span>
      {clamped && (
        <button
          onClick={() => setExpanded(true)}
          className="absolute bottom-0 right-0 text-[#A7B0C8] text-sm bg-[#141821] pl-6"
          style={{
            backgroundImage: 'linear-gradient(to right, transparent, #141821 30%)',
            backgroundClip: 'padding-box',
          }}
        >
          more...
        </button>
      )}
    </p>
  );
}

/** Standalone share button — must be outside Home to avoid remount on every render. */
function ShareButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    if (navigator.share && window.location.protocol === 'https:') {
      try { await navigator.share({ url }); setCopied(true); setTimeout(() => setCopied(false), 2000); return; } catch { /* fall through */ }
    }
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch { /* fall through */ }
    }
    // execCommand fallback
    try {
      const el = document.createElement('textarea');
      el.value = url;
      el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch { /* nothing more to try */ }
  };

  return (
    <button
      onClick={handleShare}
      aria-label={copied ? 'Link copied' : 'Share post'}
      className={`flex items-center gap-1.5 transition-colors ${copied ? 'text-[#1DB954]' : 'text-[#A7B0C8]'}`}
    >
      <Share2 size={20} />
      {copied && <span className="text-xs font-medium">Copied!</span>}
    </button>
  );
}

interface HomeProps {
  onOpenProfile?: () => void;
  onChromeVisibilityChange?: (visible: boolean) => void;
}

export default function Home({ onOpenProfile, onChromeVisibilityChange }: HomeProps) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const activeFeedType = useFeedTypeFromPath();
  const [activeCommentPostUlid, setActiveCommentPostUlid] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [chromeHeight, setChromeHeight] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  // Local overrides for optimistic like UI (postId -> { liked, count })
  const [likeOverrides, setLikeOverrides] = useState<Record<string, { liked: boolean; count: number }>>({});
  // Local overrides for optimistic bookmark UI (postId -> bookmarked)
  const [bookmarkOverrides, setBookmarkOverrides] = useState<Record<string, boolean>>({});

  const chromeRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const storiesRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const commentModalRef = useRef<HTMLDivElement>(null);
  const notificationBellRef = useRef<HTMLButtonElement>(null);
  const maxTotalSizeRef = useRef(0);

  // Data hooks
  const {
    data: feedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isFeedLoading,
    error: feedError,
    isError: isFeedError,
  } = useFeedInfinite(activeFeedType);

  const { data: suggestedUsers } = useUserSuggestions(8);

  // Mutations
  const likeMutation = useLikePost();
  const bookmarkMutation = useBookmarkPost();
  const followMutation = useFollowUser();

  // Detect touch device once — must be before feedItems
  const isTouchDevice = useRef(
    typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  );

  // Flatten paginated feed into a single array
  const posts = feedData?.pages.flatMap((page) => page.posts) ?? [];

  // On desktop: group portrait videos into 4-col shelves.
  // On mobile (touch device): keep as individual posts so snap handles one-at-a-time swipe.
  const feedItems = !isTouchDevice.current
    ? groupFeedItems(posts)
    : posts.map((post) => ({ kind: 'post' as const, post }));

  // Virtual feed — only renders visible posts + overscan buffer
  // Pass a synthetic Post[] derived from feedItems so virtualizer tracks count correctly
  const syntheticPosts = feedItems.map((item) =>
    item.kind === 'post' ? item.post : { ...item.posts[0], id: item.id }
  );

  const {
    scrollElementRef,
    virtualItems,
    totalSize,
    measureElement,
    virtualizer,
  } = useVirtualFeed({
    posts: syntheticPosts,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    overscan: 5,
  });

  // Snap-to-post + hide/show chrome — single scroll handler so snap
  // scrolls don't accidentally toggle chrome visibility.
  useEffect(() => {
    const scrollEl = scrollElementRef.current;
    if (!scrollEl) return;

    let snapTimer: ReturnType<typeof setTimeout>;
    let isSnapping = false;
    let lastScrollTop = scrollEl.scrollTop;
    const DIR_THRESHOLD = 12;

    const handleSnapEnd = () => {
      // Snap only on touch/mobile devices — never on desktop
      if (!isTouchDevice.current || window.innerWidth >= 768) return;
      if (isSnapping) return;
      const items = virtualizer.getVirtualItems();
      if (items.length === 0) return;

      const scrollTop = scrollEl.scrollTop;
      let closest = items[0];
      let closestDist = Math.abs(closest.start - scrollTop);
      for (const item of items) {
        const dist = Math.abs(item.start - scrollTop);
        if (dist < closestDist) {
          closest = item;
          closestDist = dist;
        }
      }

      if (closestDist > 8) {
        isSnapping = true;
        scrollEl.scrollTo({ top: closest.start, behavior: 'smooth' });
        setTimeout(() => {
          isSnapping = false;
          lastScrollTop = scrollEl.scrollTop;
        }, 500);
      }
    };

    const handleScroll = () => {
      // During a snap animation, ignore all scroll events
      if (isSnapping) return;

      const scrollTop = scrollEl.scrollTop;
      const delta = scrollTop - lastScrollTop;

      // Chrome show/hide based on scroll direction
      if (Math.abs(delta) >= DIR_THRESHOLD) {
        if (delta > 0 && scrollTop > 80) {
          setChromeVisible(false);
          onChromeVisibilityChange?.(false);
        } else if (delta < 0) {
          setChromeVisible(true);
          onChromeVisibilityChange?.(true);
        }
        lastScrollTop = scrollTop;
      }

      // Debounce snap
      clearTimeout(snapTimer);
      snapTimer = setTimeout(handleSnapEnd, 150);
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      clearTimeout(snapTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualizer, isFeedLoading, onChromeVisibilityChange]);

  // Entry animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' }
      );

      gsap.fromTo(
        storiesRef.current?.children || [],
        { x: 40, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, stagger: 0.04, ease: 'power2.out', delay: 0.1 }
      );

      gsap.fromTo(
        filtersRef.current?.children || [],
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.35, stagger: 0.03, ease: 'power2.out', delay: 0.2 }
      );
    });

    return () => ctx.revert();
  }, [activeFeedType]);

  // Measure chrome height for transform-based collapse
  useEffect(() => {
    const el = chromeRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setChromeHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);


  const handleLike = useCallback((post: Post) => {
    const postId = post.id;
    const override = likeOverrides[postId];
    const currentLiked = override?.liked ?? (post.userReaction === 'heart');
    const currentCount = override?.count ?? getLikesCount(post);

    // Optimistic update
    setLikeOverrides(prev => ({
      ...prev,
      [postId]: {
        liked: !currentLiked,
        count: currentLiked ? Math.max(0, currentCount - 1) : currentCount + 1,
      },
    }));

    likeMutation.mutate(
      { ulid: postId, liked: currentLiked },
      {
        onError: () => {
          // Revert on error
          setLikeOverrides(prev => ({
            ...prev,
            [postId]: { liked: currentLiked, count: currentCount },
          }));
        },
      }
    );
  }, [likeMutation, likeOverrides]);

  const handleBookmark = useCallback((post: Post) => {
    const postId = post.id;
    const currentBookmarked = bookmarkOverrides[postId] ?? post.postEngagement?.bookmarked ?? (post as Record<string, unknown>).isBookmarked ?? false;

    // Optimistic update
    setBookmarkOverrides(prev => ({
      ...prev,
      [postId]: !currentBookmarked,
    }));

    bookmarkMutation.mutate(
      { ulid: postId, bookmarked: currentBookmarked },
      {
        onError: () => {
          // Revert on error
          setBookmarkOverrides(prev => ({
            ...prev,
            [postId]: currentBookmarked,
          }));
        },
      }
    );
  }, [bookmarkMutation, bookmarkOverrides]);

  const handleFollow = useCallback((username: string, isFollowing: boolean) => {
    followMutation.mutate({ username, isFollowing });
  }, [followMutation]);

  const openComments = (postUlid: string) => {
    setActiveCommentPostUlid(postUlid);
  };

  const navigateToPost = (postUlid: string) => {
    navigate(`/post/${postUlid}`);
  };

  const closeComments = () => {
    setActiveCommentPostUlid(null);
  };

  const renderFollowButton = (username: string, isFollowing: boolean, size: 'sm' | 'md' = 'sm') => (
    <button
      onClick={() => handleFollow(username, isFollowing)}
      className={`flex items-center gap-1 rounded-full font-medium transition-all ${
        isFollowing
          ? 'bg-[#141821] text-[#A7B0C8] border border-white/[0.08]'
          : 'bg-[#A38BFF] text-[#0B0D10]'
      } ${size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-1.5 text-sm'}`}
    >
      {isFollowing ? (
        <><UserCheck size={size === 'sm' ? 12 : 14} /><span>Following</span></>
      ) : (
        <><UserPlus size={size === 'sm' ? 12 : 14} /><span>Follow</span></>
      )}
    </button>
  );

  const renderPost = (post: Post) => {
    const author = getPostUser(post);
    const isFollowing = (post.user as Record<string, unknown> | undefined)?.isFollowing as boolean | undefined;

    return (
      <PostCard
        key={post.id}
        post={post}
        liked={likeOverrides[post.id]?.liked}
        likeCount={likeOverrides[post.id]?.count}
        bookmarked={bookmarkOverrides[post.id]}
        onLike={handleLike}
        onBookmark={handleBookmark}
        onComment={openComments}
        onShare={(id) => {
          const url = `${window.location.origin}/post/${id}`;
          if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).catch(() => {});
        }}
        headerAction={author.username ? renderFollowButton(author.username, isFollowing ?? false) : undefined}
      />
    );
  };

  const renderSong = (post: Post) => {
    const author = getPostUser(post);
    const isFollowing = (post.user as Record<string, unknown> | undefined)?.isFollowing as boolean | undefined;
    const override = likeOverrides[post.id];
    const liked = override?.liked ?? (post.userReaction === 'heart');
    const albumArt = getPostImage(post);

    return (
      <div key={post.id} className="bg-[#141821] rounded-2xl p-4 mb-3.5">
        <div className="flex items-center gap-4">
          <div className="relative">
            {albumArt ? (
              <img src={albumArt} alt={post.title ?? ''} className="w-20 h-20 rounded-xl object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-[#A38BFF]/20 flex items-center justify-center">
                <Play size={24} className="text-[#A38BFF]" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/20 rounded-xl" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-[#A38BFF]" />
              <span className="text-xs text-[#A38BFF]">SONG</span>
            </div>
            <p className="font-semibold text-[#F5F7FF]">{post.title || 'Untitled'}</p>
            <p className="text-sm text-[#A7B0C8]">{post.artist || author.name}</p>
            <div className="flex items-center gap-3 mt-2">
              <button aria-label="Play" className="w-8 h-8 rounded-full bg-[#A38BFF] flex items-center justify-center">
                <Play size={14} fill="#0B0D10" className="text-[#0B0D10] ml-0.5" />
              </button>
              <button
                aria-label={liked ? 'Unlike' : 'Like'}
                onClick={() => handleLike(post)}
                className={liked ? 'text-[#A38BFF]' : 'text-[#A7B0C8] hover:text-[#F5F7FF]'}
              >
                <Heart size={18} fill={liked ? '#A38BFF' : 'none'} />
              </button>
              <button aria-label="Comment" onClick={() => openComments(post.id)} className="text-[#A7B0C8] hover:text-[#F5F7FF]">
                <MessageCircle size={18} />
              </button>
              {post.duration != null && (
                <span className="text-xs text-[#A7B0C8]">
                  {Math.floor(post.duration / 60)}:{String(Math.floor(post.duration % 60)).padStart(2, '0')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
          <p className="text-xs text-[#A7B0C8]">Artist: {post.artist || author.name}</p>
          {author.username && renderFollowButton(author.username, isFollowing ?? false, 'sm')}
        </div>
      </div>
    );
  };

  const renderVideo = (post: Post) => {
    const author = getPostUser(post);
    const isFollowing = (post.user as Record<string, unknown> | undefined)?.isFollowing as boolean | undefined;
    const videoSource = getVideoSource(post);
    const thumbnail = videoSource?.poster || getVideoThumbnail(post);

    return (
      <div key={post.id} className="bg-[#141821] rounded-2xl p-4 mb-3.5">
        <div className="flex items-center gap-3 mb-3">
          <div onClick={() => author.username && navigate(`/profile/${author.username}`)} className="cursor-pointer">
            {author.avatar ? (
              <img src={author.avatar} alt={author.name} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#A38BFF]/20 flex items-center justify-center">
                <User size={14} className="text-[#A38BFF]" />
              </div>
            )}
          </div>
          <p onClick={() => author.username && navigate(`/profile/${author.username}`)} className="font-semibold text-sm text-[#F5F7FF] cursor-pointer hover:underline">{author.name}</p>
          {author.username && renderFollowButton(author.username, isFollowing ?? false)}
          <span className="text-xs text-[#FF4D4D] ml-auto">Video</span>
        </div>
        {videoSource ? (
          <div className={videoSource.isPortrait ? 'max-h-[460px] overflow-hidden rounded-xl' : ''}>
            <VideoPlayer
              src={videoSource.src}
              poster={videoSource.poster}
              aspectRatio={videoSource.isPortrait ? '9/16' : '16/9'}
            />
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden">
            {thumbnail ? (
              <img src={thumbnail} alt={post.title ?? ''} className="w-full aspect-video object-cover" />
            ) : (
              <div className="w-full aspect-video bg-[#0B0D10] flex items-center justify-center">
                <Play size={32} className="text-[#A7B0C8]" />
              </div>
            )}
          </div>
        )}
        {(post.title || getPostContent(post)) && (
          <ExpandableText text={post.title || getPostContent(post)} className="text-sm font-medium text-[#F5F7FF] mt-3" />
        )}
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-[#A7B0C8]">
            {formatCount(post.postEngagement?.viewCount ?? 0)} views{post.createdAt ? ` \u2022 ${timeAgo(post.createdAt)}` : ''}
          </p>
          <div className="flex items-center gap-3">
            <button aria-label="Comment" onClick={() => openComments(post.id)} className="flex items-center gap-1 text-[#A7B0C8]">
              <MessageCircle size={16} />
              <span className="text-xs">{formatCount(post.commentCount ?? 0)}</span>
            </button>
            <ShareButton url={`${window.location.origin}/post/${post.id}`} />
          </div>
        </div>
      </div>
    );
  };

  const renderBurst = (post: Post) => {
    const author = getPostUser(post);
    const isFollowing = (post.user as Record<string, unknown> | undefined)?.isFollowing as boolean | undefined;
    const videoSource = getVideoSource(post);
    const thumbnail = videoSource?.poster || getVideoThumbnail(post);

    return (
      <div key={post.id} className="bg-[#141821] rounded-2xl p-4 mb-3.5">
        <div className="flex items-center gap-3 mb-3">
          <div onClick={() => author.username && navigate(`/profile/${author.username}`)} className="cursor-pointer">
            {author.avatar ? (
              <img src={author.avatar} alt={author.name} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#A38BFF]/20 flex items-center justify-center">
                <User size={14} className="text-[#A38BFF]" />
              </div>
            )}
          </div>
          <p onClick={() => author.username && navigate(`/profile/${author.username}`)} className="font-semibold text-sm text-[#F5F7FF] cursor-pointer hover:underline">{author.name}</p>
          {author.username && renderFollowButton(author.username, isFollowing ?? false)}
          <span className="text-xs text-[#25F4EE] ml-auto">Short</span>
        </div>
        {videoSource ? (
          <div className="max-h-[460px] overflow-hidden rounded-xl">
            <VideoPlayer
              src={videoSource.src}
              poster={videoSource.poster}
              aspectRatio="9/16"
            />
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden">
            {thumbnail ? (
              <img src={thumbnail} alt={post.title ?? ''} className="w-full aspect-[9/16] max-h-80 object-cover" />
            ) : (
              <div className="w-full aspect-[9/16] max-h-80 bg-[#0B0D10] flex items-center justify-center">
                <Play size={28} className="text-[#A7B0C8]" />
              </div>
            )}
          </div>
        )}
        {(post.title || getPostContent(post)) && (
          <ExpandableText text={post.title || getPostContent(post)} className="text-sm font-medium text-[#F5F7FF] mt-3" />
        )}
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-[#A7B0C8]">{formatCount(post.postEngagement?.viewCount ?? 0)} views</p>
          <div className="flex items-center gap-3">
            <button aria-label="Comment" onClick={() => openComments(post.id)} className="flex items-center gap-1 text-[#A7B0C8]">
              <MessageCircle size={16} />
              <span className="text-xs">{formatCount(post.commentCount ?? 0)}</span>
            </button>
            <ShareButton url={`${window.location.origin}/post/${post.id}`} />
          </div>
        </div>
      </div>
    );
  };

  const renderFeedItem = (post: Post) => {
    switch (post.type) {
      case 'SONG':
        return renderSong(post);
      case 'VIDEO':
        return renderVideo(post);
      case 'SHORT':
      case 'BURST':
        return renderBurst(post);
      default:
        return renderPost(post);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden relative">
      {/* Top Chrome — slides up on scroll down, slides back on scroll up */}
      <div
        ref={chromeRef}
        className="absolute top-0 left-0 right-0 z-40 bg-[#0B0D10]"
        style={{
          transform: chromeVisible ? 'translateY(0)' : `translateY(-100%)`,
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'transform',
        }}
      >
      {/* Top Bar */}
      <div
        ref={headerRef}
        className="bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 pt-4 pb-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>
            Pulse
          </h1>
          <div className="flex items-center gap-4">
            <button aria-label="Search" onClick={() => navigate('/search')} className="text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors">
              <Search size={22} />
            </button>
            <button
              ref={notificationBellRef}
              aria-label="Notifications"
              onClick={() => setNotificationPanelOpen(true)}
              className="text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors relative"
            >
              <Bell size={22} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#A38BFF] rounded-full animate-pulse" />
            </button>
            <button
              aria-label="Profile"
              onClick={onOpenProfile}
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#A38BFF]/50 hover:border-[#A38BFF] transition-all cursor-pointer active:scale-95 flex-shrink-0"
            >
              {currentUser?.avatarUrl ? (
                <img src={processAvatarUrl(currentUser.avatarUrl) || ''} alt="Profile" className="w-full h-full object-cover pointer-events-none" />
              ) : (
                <div className="w-full h-full bg-[#A38BFF]/20 flex items-center justify-center">
                  <User size={16} className="text-[#A38BFF]" />
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stories Row — suggested users */}
      <div ref={storiesRef} className="flex gap-4 px-4 py-4 overflow-x-auto hide-scrollbar flex-shrink-0">
        {/* Current user "Your story" */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div className="relative w-[72px] h-[72px] rounded-full p-[2px] bg-gradient-to-br from-[#A38BFF] to-[#7B68EE]">
            {currentUser?.avatarUrl ? (
              <img src={processAvatarUrl(currentUser.avatarUrl) || ''} alt="Your story" className="w-full h-full rounded-full object-cover border-2 border-[#0B0D10]" />
            ) : (
              <div className="w-full h-full rounded-full bg-[#141821] border-2 border-[#0B0D10] flex items-center justify-center">
                <User size={24} className="text-[#A38BFF]" />
              </div>
            )}
            <div className="absolute bottom-0 right-0 w-5 h-5 bg-[#A38BFF] rounded-full flex items-center justify-center border-2 border-[#0B0D10]">
              <Plus size={12} strokeWidth={3} className="text-[#0B0D10]" />
            </div>
          </div>
          <span className="text-xs text-[#A7B0C8] mt-2 truncate w-16 text-center">Your story</span>
        </div>

        {/* Suggested users */}
        {suggestedUsers?.map((su) => (
          <div key={su.ulid} onClick={() => su.username && navigate(`/profile/${su.username}`)} className="flex flex-col items-center flex-shrink-0 cursor-pointer">
            <div className="relative w-[72px] h-[72px] rounded-full p-[2px] bg-gradient-to-br from-[#A38BFF] to-[#7B68EE]">
              {su.avatar ? (
                <img src={processAvatarUrl(su.avatar) || ''} alt={su.name} className="w-full h-full rounded-full object-cover border-2 border-[#0B0D10]" />
              ) : (
                <div className="w-full h-full rounded-full bg-[#141821] border-2 border-[#0B0D10] flex items-center justify-center">
                  <User size={24} className="text-[#A7B0C8]" />
                </div>
              )}
            </div>
            <span className="text-xs text-[#A7B0C8] mt-2 truncate w-16 text-center">{su.name}</span>
          </div>
        ))}
      </div>

      {/* Filter Chips */}
      <div
        ref={filtersRef}
        className="flex gap-2 px-4 py-2 overflow-x-auto hide-scrollbar flex-shrink-0 z-30 bg-[#0B0D10]/95 backdrop-blur-xl"
      >
        {filterChips.map((chip) => (
          <button
            key={chip.label}
            onClick={() => navigate(chip.path)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              activeFeedType === chip.feedType
                ? 'bg-[#A38BFF]/15 text-[#A38BFF] border border-[#A38BFF]/35'
                : 'bg-[#141821] text-[#A7B0C8] border border-white/[0.08] hover:bg-[#1A1F2A]'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>
      </div>{/* end top chrome wrapper */}

      {/* Virtualized Feed */}
      {isFeedLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={32} className="animate-spin text-[#A38BFF]" />
        </div>
      ) : isFeedError ? (
        <div className="text-center py-12">
          <p className="text-red-400">Feed error</p>
          <p className="text-sm text-red-400/70 mt-1">{feedError?.message || 'Unknown error'}</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-[#A7B0C8]">No posts yet</p>
          <p className="text-sm text-[#A7B0C8] mt-1">Follow people to see their posts here</p>
        </div>
      ) : (
        <div
          ref={scrollElementRef}
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ paddingTop: chromeHeight }}
        >
          <div
            className="relative px-4 pt-4"
            style={{ height: `${(maxTotalSizeRef.current = Math.max(maxTotalSizeRef.current, totalSize))}px` }}
          >
            {virtualItems.map((virtualItem) => {
              const item = feedItems[virtualItem.index];
              if (!item) return null;
              return (
                <div
                  key={item.kind === 'post' ? item.post.id : item.id}
                  data-index={virtualItem.index}
                  ref={measureElement}
                  className="absolute left-0 right-0 px-4"
                  style={{ top: `${virtualItem.start}px` }}
                >
                  {item.kind === 'shelf' ? (
                    <PortraitVideoShelf
                      posts={item.posts}
                      onNavigate={navigateToPost}
                      onComment={openComments}
                      onShare={(id) => {
                        const url = `${window.location.origin}/post/${id}`;
                        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).catch(() => {});
                      }}
                    />
                  ) : (
                    renderFeedItem(item.post)
                  )}
                </div>
              );
            })}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center py-6">
              <Loader2 size={24} className="animate-spin text-[#A38BFF]" />
            </div>
          )}
        </div>
      )}

      {/* Comments Modal */}
      {activeCommentPostUlid !== null && (
        <CommentModal
          postUlid={activeCommentPostUlid}
          onClose={closeComments}
          onProfileClick={(username) => navigate(`/profile/${username}`)}
        />
      )}

      {/* FAB — compose post */}
      <button
        onClick={() => setComposerOpen(true)}
        className="fixed bottom-24 right-5 w-14 h-14 bg-[#A38BFF] rounded-full flex items-center justify-center shadow-lg shadow-[#A38BFF]/30 active:scale-95 transition-transform z-30"
        aria-label="Create post"
      >
        <Pencil size={22} className="text-[#0B0D10]" />
      </button>

      {/* Post composer */}
      {composerOpen && (
        <PostComposer
          onClose={() => setComposerOpen(false)}
          onSuccess={() => setComposerOpen(false)}
        />
      )}

      {/* Notification panel */}
      <NotificationPanel
        isOpen={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
        onSeeAll={() => {
          setNotificationPanelOpen(false);
          navigate('/notifications');
        }}
        anchorRef={notificationBellRef}
      />
    </div>
  );
}
